import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material";
import {
  Stack,
  type SxProps,
  TableCell,
  Tooltip,
  Typography
} from "@mui/material";
import React, {
  type CSSProperties,
  type Dispatch,
  type SetStateAction,
} from "react";

import type { CustomColumn } from "../..";
import { sort } from "../../processor";

export interface DataTableHeaderProps<T> {
  customColumns: CustomColumn;
  item: string;
  filteredData: T[];
  setFilteredData: Dispatch<SetStateAction<T[]>>;
  headers: string[];
  order: Record<string, any>;
  setOrder: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  selectAll: boolean;
  setSelectAll: React.Dispatch<React.SetStateAction<boolean>>;
  sx?: SxProps;
  headerTextColor?: string;
  defaultColumnWidth?: CSSProperties["width"];
}

export function Header<T extends Record<string, any> = {}>({
  customColumns,
  item,
  filteredData,
  setFilteredData,
  headers,
  order,
  setOrder,
  selectAll,
  setSelectAll,
  sx,
  headerTextColor,
  defaultColumnWidth,
}: DataTableHeaderProps<T>) {
  const onChecked = () => setSelectAll(!selectAll);
  const type =
    customColumns?.[item] && customColumns?.[item]?.type
      ? customColumns?.[item]?.type
      : "text";

  // if (item === "hasCheckBox")
  //   return (
  //     <Checkbox
  //       checked={selectAll}
  //       onChange={onChecked}
  //       size="small"
  //       id="select-all-box"
  //     />
  //   );

  // Format header text by adding spaces between words
  const formatHeaderText = (text: string) => {
    if (customColumns?.[text]?.headerName) {
      return customColumns?.[text]?.headerName;
    }
    
    // Replace underscores with spaces
    let formattedText = text.replace(/_/g, ' ');
    
    // Handle camelCase by adding spaces before uppercase letters
    formattedText = formattedText.replace(/([a-z])([A-Z])/g, '$1 $2');
    
    // Handle ALL_CAPS by adding spaces
    formattedText = formattedText.replace(/([A-Z])([A-Z][a-z])/g, '$1 $2');
    
    // Clean up any extra spaces
    formattedText = formattedText.replace(/\s+/g, ' ').trim();
    
    // Capitalize first letter of each word
    formattedText = formattedText.split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
    
    return formattedText;
  };

  const sorter = (order: "ASC" | "DSC") =>
    sort(filteredData, setFilteredData, type, item, headers, order);
  const ascend = () => {
    customColumns?.[item]?.sortComparator
      ? customColumns?.[item]?.sortComparator(filteredData)
      : sorter("ASC");

    setOrder({ [item]: "ASC" });
  };
  const descend = () => {
    customColumns?.[item]?.sortComparator
      ? customColumns?.[item]?.sortComparator(filteredData)
      : sorter("DSC");
    setOrder({ [item]: "DSC" });
  };

  const headerText = formatHeaderText(item);
  
  // Check if this is a numeric column to apply right alignment
  const isNumericColumn = customColumns?.[item]?.type === 'number' || 
    customColumns?.[item]?.isNumeric === true;

  return (
    <TableCell
      key={`${item}`}
      component="div"
      style={{ borderRight: "1px solid #e0e0e0", width: "100%" }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        width={customColumns?.[item]?.minWidth ?? defaultColumnWidth}
        sx={{
          padding: "10px 6px",
          backgroundColor: "#f5f7fa"
        }}
      >
        <Stack width="100%">
          <Tooltip title={headerText}>
            <Typography
              sx={{
                margin: 0,
                fontSize: "0.85rem",
                textAlign: isNumericColumn ? "right" : "start",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontWeight: "600",
                color: headerTextColor || "#424242",
                letterSpacing: "0.02em",
                padding: "4px 0",
                ...(sx as object || {}),
                ...(customColumns?.[item]?.headerProps?.sx || {})
              }}
            >
              {headerText}
            </Typography>
          </Tooltip>
        </Stack>
        <Stack>
          <ArrowDropUp
            color={order[item] === "ASC" ? "disabled" : "primary"}
            fontSize="small"
            onClick={ascend}
            sx={{ visibility: type !== "component" ? "visible" : "hidden" }}
          />
          <ArrowDropDown
            color={order[item] === "DSC" ? "disabled" : "primary"}
            fontSize="small"
            onClick={descend}
            sx={{ visibility: type !== "component" ? "visible" : "hidden" }}
          />
        </Stack>
      </Stack>
    </TableCell>
  );
}
